import React, { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
export function AnimatedBackground() {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const frameRef = useRef<number>(0);
  useEffect(() => {
    if (!containerRef.current) return;
    // Scene setup
    const scene = new THREE.Scene();
    sceneRef.current = scene;
    // Camera
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 30;
    cameraRef.current = camera;
    // Renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    // Floating cubes
    const cubes: THREE.Mesh[] = [];
    const cubeGeometry = new THREE.BoxGeometry(1, 1, 1);
    for (let i = 0; i < 15; i++) {
      const cubeMaterial = new THREE.MeshPhysicalMaterial({
        color: i % 3 === 0 ? 0xd4af37 : i % 3 === 1 ? 0x4169e1 : 0xffffff,
        transparent: true,
        opacity: 0.15 + Math.random() * 0.1,
        roughness: 0.1,
        metalness: 0.8,
        transmission: 0.6,
        thickness: 0.5
      });
      const cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
      cube.position.set((Math.random() - 0.5) * 60, (Math.random() - 0.5) * 40, (Math.random() - 0.5) * 30 - 10);
      cube.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      const scale = 0.5 + Math.random() * 2;
      cube.scale.set(scale, scale, scale);
      cube.userData = {
        rotationSpeed: {
          x: (Math.random() - 0.5) * 0.01,
          y: (Math.random() - 0.5) * 0.01,
          z: (Math.random() - 0.5) * 0.01
        },
        floatSpeed: 0.5 + Math.random() * 0.5,
        floatOffset: Math.random() * Math.PI * 2,
        originalY: cube.position.y
      };
      scene.add(cube);
      cubes.push(cube);
    }
    // Golden energy lines
    const linesMaterial = new THREE.LineBasicMaterial({
      color: 0xd4af37,
      transparent: true,
      opacity: 0.3
    });
    const lines: THREE.Line[] = [];
    for (let i = 0; i < 8; i++) {
      const points: THREE.Vector3[] = [];
      const startX = (Math.random() - 0.5) * 80;
      const startY = (Math.random() - 0.5) * 50;
      const startZ = -20 + Math.random() * 10;
      for (let j = 0; j < 50; j++) {
        points.push(new THREE.Vector3(startX + Math.sin(j * 0.2 + i) * 10, startY + j * 0.8 - 20, startZ + Math.cos(j * 0.15 + i) * 5));
      }
      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(lineGeometry, linesMaterial.clone());
      line.userData = {
        offset: i * 0.5
      };
      scene.add(line);
      lines.push(line);
    }
    // Floating particles
    const particlesGeometry = new THREE.BufferGeometry();
    const particleCount = 200;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 80;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 60;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 40 - 10;
      // Mix of gold, white, and indigo particles
      const colorChoice = Math.random();
      if (colorChoice < 0.5) {
        // Gold
        colors[i * 3] = 0.83;
        colors[i * 3 + 1] = 0.69;
        colors[i * 3 + 2] = 0.22;
      } else if (colorChoice < 0.8) {
        // White/Pearl
        colors[i * 3] = 1;
        colors[i * 3 + 1] = 0.98;
        colors[i * 3 + 2] = 0.94;
      } else {
        // Indigo
        colors[i * 3] = 0.25;
        colors[i * 3 + 1] = 0.41;
        colors[i * 3 + 2] = 0.88;
      }
      sizes[i] = Math.random() * 2 + 0.5;
    }
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    particlesGeometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 0.6,
      blending: THREE.AdditiveBlending
    });
    const particles = new THREE.Points(particlesGeometry, particlesMaterial);
    scene.add(particles);
    // Central aura glow (blue/white)
    const auraGeometry = new THREE.SphereGeometry(8, 32, 32);
    const auraMaterial = new THREE.MeshBasicMaterial({
      color: 0x4169e1,
      transparent: true,
      opacity: 0.05
    });
    const aura = new THREE.Mesh(auraGeometry, auraMaterial);
    aura.position.set(0, 0, -15);
    scene.add(aura);
    const innerAuraGeometry = new THREE.SphereGeometry(4, 32, 32);
    const innerAuraMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.08
    });
    const innerAura = new THREE.Mesh(innerAuraGeometry, innerAuraMaterial);
    innerAura.position.set(0, 0, -15);
    scene.add(innerAura);
    // Ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambientLight);
    // Point lights for golden glow
    const goldLight = new THREE.PointLight(0xd4af37, 1, 50);
    goldLight.position.set(10, 10, 10);
    scene.add(goldLight);
    const blueLight = new THREE.PointLight(0x4169e1, 0.5, 40);
    blueLight.position.set(-10, -5, 5);
    scene.add(blueLight);
    // Animation
    let time = 0;
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      time += 0.005;
      // Animate cubes
      cubes.forEach(cube => {
        cube.rotation.x += cube.userData.rotationSpeed.x;
        cube.rotation.y += cube.userData.rotationSpeed.y;
        cube.rotation.z += cube.userData.rotationSpeed.z;
        cube.position.y = cube.userData.originalY + Math.sin(time * cube.userData.floatSpeed + cube.userData.floatOffset) * 2;
      });
      // Animate lines opacity
      lines.forEach((line, i) => {
        const material = line.material as THREE.LineBasicMaterial;
        material.opacity = 0.15 + Math.sin(time * 2 + line.userData.offset) * 0.15;
      });
      // Animate particles
      const particlePositions = particlesGeometry.attributes.position.array as Float32Array;
      for (let i = 0; i < particleCount; i++) {
        particlePositions[i * 3 + 1] += Math.sin(time + i * 0.1) * 0.02;
        particlePositions[i * 3] += Math.cos(time * 0.5 + i * 0.05) * 0.01;
      }
      particlesGeometry.attributes.position.needsUpdate = true;
      // Animate aura
      aura.scale.setScalar(1 + Math.sin(time) * 0.1);
      innerAura.scale.setScalar(1 + Math.sin(time * 1.5) * 0.15);
      auraMaterial.opacity = 0.03 + Math.sin(time) * 0.02;
      innerAuraMaterial.opacity = 0.05 + Math.sin(time * 1.5) * 0.03;
      // Subtle camera movement
      camera.position.x = Math.sin(time * 0.3) * 2;
      camera.position.y = Math.cos(time * 0.2) * 1;
      camera.lookAt(0, 0, -10);
      renderer.render(scene, camera);
    };
    animate();
    // Handle resize
    const handleResize = () => {
      if (!containerRef.current) return;
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(frameRef.current);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);
  return <>
      <div ref={containerRef} className="fixed inset-0 z-0 pointer-events-none" style={{
      background: 'linear-gradient(to bottom, #0A0A0A 0%, #050510 50%, #0A0A0A 100%)'
    }} data-id="element-37" />
      {/* Bottom fog/mist effect */}
      <div className="fixed bottom-0 left-0 right-0 h-64 z-[1] pointer-events-none" style={{
      background: 'linear-gradient(to top, rgba(212, 175, 55, 0.08) 0%, rgba(65, 105, 225, 0.03) 30%, transparent 100%)'
    }} data-id="element-38" />
      {/* Top vignette */}
      <div className="fixed inset-0 z-[1] pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, transparent 0%, rgba(10, 10, 10, 0.4) 100%)'
    }} data-id="element-39" />
    </>;
}